import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  CardStatusFilter,
  DoBoxSortField,
  DoBoxSortOrder,
  DueDateFilter,
  PriorityFilter,
} from '../../components/DoBoxFilters/types';
import { useTeamStore } from '../Team/useTeamStore';
import { createIndexedDBStorage } from '../utils/indexedDBAdapter';

// Read-only store access (NOT useActiveTeamId — this runs inside a zustand
// updater, outside React render, where hooks throw). Mirrors the selector's
// activeTeamId ?? userTeamIds[0] fallback so the stamp matches the comparison.
function getActiveTeamId(): string | null {
  const teamState = useTeamStore.getState();
  return teamState.activeTeamId ?? teamState.userTeamIds?.[0] ?? null;
}

export interface DoBoxFilterSortState {
  selectedFilterLabelIds: string[];
  showNoLabelsFilter: boolean;
  selectedDueDateFilters: DueDateFilter[];
  selectedPriorityFilters: PriorityFilter[];
  selectedCardStatusFilter: CardStatusFilter;
  showNoMembersFilter: boolean;
  selectedFilterMemberIds: string[];
  unreadOnly: boolean;
  sortField: DoBoxSortField;
  sortOrder: DoBoxSortOrder;
}

interface DoBoxFilterSortActions {
  toggleFilterLabel: (labelId: string) => void;
  toggleNoLabelsFilter: () => void;
  toggleDueDateFilter: (filter: DueDateFilter) => void;
  togglePriorityFilter: (filter: PriorityFilter) => void;
  toggleCardStatusFilter: (filter: CardStatusFilter) => void;
  toggleNoMembersFilter: () => void;
  toggleFilterMemberId: (memberId: string) => void;
  toggleUnreadOnly: () => void;
  clearAllFilters: () => void;
  clearMemberFilters: () => void;
  retainMemberFilters: (allowedIds: string[]) => void;
  setSortField: (field: DoBoxSortField) => void;
  setSortOrder: (order: DoBoxSortOrder) => void;
  initSort: (config: {
    sortField: DoBoxSortField;
    sortOrder: DoBoxSortOrder;
  }) => void;
}

interface DoBoxPersistenceState {
  _hasHydrated: boolean;
  lastActiveTaskGroupId: string | null;
  // Owner team of the remembered task group — NOT the user's current active team.
  lastActiveTeamId: string | null;
  taskGroupFilterSortMap: Record<string, DoBoxFilterSortState>;
}

interface DoBoxPersistenceActions {
  setHasHydrated: (value: boolean) => void;
  switchTaskGroupAndRestore: (
    taskGroupId: string,
    ownerTeamId?: string | null,
  ) => void;
  removeTaskGroupSnapshot: (taskGroupId: string) => void;
}

export type DoBoxFilterSortStore = DoBoxFilterSortState &
  DoBoxFilterSortActions &
  DoBoxPersistenceState &
  DoBoxPersistenceActions;

export const INITIAL_FILTER_SORT_STATE: DoBoxFilterSortState = {
  selectedFilterLabelIds: [],
  showNoLabelsFilter: false,
  selectedDueDateFilters: [],
  selectedPriorityFilters: [],
  selectedCardStatusFilter: CardStatusFilter.UNRESOLVED,
  showNoMembersFilter: false,
  selectedFilterMemberIds: [],
  unreadOnly: false,
  sortField: DoBoxSortField.CREATED_AT,
  sortOrder: DoBoxSortOrder.ASC,
};

export function selectHasActiveFilters(s: DoBoxFilterSortState): boolean {
  return (
    s.selectedFilterLabelIds.length > 0 ||
    s.showNoLabelsFilter ||
    s.selectedDueDateFilters.length > 0 ||
    s.selectedPriorityFilters.length > 0 ||
    s.selectedCardStatusFilter !== CardStatusFilter.UNRESOLVED ||
    s.showNoMembersFilter ||
    s.selectedFilterMemberIds.length > 0 ||
    s.unreadOnly
  );
}

function toggleArrayItem<T>(array: T[], item: T): T[] {
  return array.includes(item)
    ? array.filter((existing) => existing !== item)
    : [...array, item];
}

function resolveCardStatusFilter(
  current: CardStatusFilter,
  next: CardStatusFilter,
): CardStatusFilter {
  return current === next ? CardStatusFilter.UNRESOLVED : next;
}

function extractFilterSortState(
  state: DoBoxFilterSortState,
): DoBoxFilterSortState {
  return {
    selectedFilterLabelIds: state.selectedFilterLabelIds,
    showNoLabelsFilter: state.showNoLabelsFilter,
    selectedDueDateFilters: state.selectedDueDateFilters,
    selectedPriorityFilters: state.selectedPriorityFilters,
    selectedCardStatusFilter: state.selectedCardStatusFilter,
    showNoMembersFilter: state.showNoMembersFilter,
    selectedFilterMemberIds: state.selectedFilterMemberIds,
    unreadOnly: state.unreadOnly,
    sortField: state.sortField,
    sortOrder: state.sortOrder,
  };
}

function withSnapshotUpdate(
  state: DoBoxFilterSortStore,
  update: Partial<DoBoxFilterSortState>,
): Partial<DoBoxFilterSortStore> {
  if (!state.lastActiveTaskGroupId) {
    return update;
  }
  const merged: DoBoxFilterSortState = {
    ...extractFilterSortState(state),
    ...update,
  };
  return {
    ...update,
    taskGroupFilterSortMap: {
      ...state.taskGroupFilterSortMap,
      [state.lastActiveTaskGroupId]: merged,
    },
  };
}

export const useDoBoxFilterSortStore = create<DoBoxFilterSortStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_FILTER_SORT_STATE,
      _hasHydrated: false,
      lastActiveTaskGroupId: null,
      lastActiveTeamId: null,
      taskGroupFilterSortMap: {},
      setHasHydrated: (value) => set({ _hasHydrated: value }),

      switchTaskGroupAndRestore: (taskGroupId, ownerTeamId) =>
        set((state) => {
          const mapWithPrevious =
            state.lastActiveTaskGroupId &&
            state.lastActiveTaskGroupId !== taskGroupId
              ? {
                  ...state.taskGroupFilterSortMap,
                  [state.lastActiveTaskGroupId]: extractFilterSortState(state),
                }
              : state.taskGroupFilterSortMap;

          const snapshot = mapWithPrevious[taskGroupId];
          const filterState =
            snapshot ?? extractFilterSortState(INITIAL_FILTER_SORT_STATE);

          return {
            ...filterState,
            lastActiveTaskGroupId: taskGroupId,
            lastActiveTeamId:
              ownerTeamId !== undefined ? ownerTeamId : getActiveTeamId(),
            taskGroupFilterSortMap: mapWithPrevious,
          };
        }),

      removeTaskGroupSnapshot: (taskGroupId) =>
        set((state) => {
          const { [taskGroupId]: _, ...rest } = state.taskGroupFilterSortMap;
          return {
            taskGroupFilterSortMap: rest,
            ...(state.lastActiveTaskGroupId === taskGroupId
              ? { lastActiveTaskGroupId: null, lastActiveTeamId: null }
              : {}),
          };
        }),

      toggleFilterLabel: (labelId) =>
        set((state) => {
          const newLabelIds = toggleArrayItem(
            state.selectedFilterLabelIds,
            labelId,
          );
          return withSnapshotUpdate(state, {
            selectedFilterLabelIds: newLabelIds,
            showNoLabelsFilter:
              newLabelIds.length > 0 ? false : state.showNoLabelsFilter,
          });
        }),

      toggleNoLabelsFilter: () =>
        set((state) => {
          const newShowNoLabels = !state.showNoLabelsFilter;
          return withSnapshotUpdate(state, {
            showNoLabelsFilter: newShowNoLabels,
            selectedFilterLabelIds: newShowNoLabels
              ? []
              : state.selectedFilterLabelIds,
          });
        }),

      toggleDueDateFilter: (filter) =>
        set((state) =>
          withSnapshotUpdate(state, {
            selectedDueDateFilters: toggleArrayItem(
              state.selectedDueDateFilters,
              filter,
            ),
          }),
        ),

      togglePriorityFilter: (filter) =>
        set((state) =>
          withSnapshotUpdate(state, {
            selectedPriorityFilters: toggleArrayItem(
              state.selectedPriorityFilters,
              filter,
            ),
          }),
        ),

      toggleCardStatusFilter: (filter) =>
        set((state) =>
          withSnapshotUpdate(state, {
            selectedCardStatusFilter: resolveCardStatusFilter(
              state.selectedCardStatusFilter,
              filter,
            ),
          }),
        ),

      toggleNoMembersFilter: () =>
        set((state) =>
          withSnapshotUpdate(state, {
            showNoMembersFilter: !state.showNoMembersFilter,
          }),
        ),

      toggleFilterMemberId: (memberId) =>
        set((state) =>
          withSnapshotUpdate(state, {
            selectedFilterMemberIds: toggleArrayItem(
              state.selectedFilterMemberIds,
              memberId,
            ),
          }),
        ),

      toggleUnreadOnly: () =>
        set((state) =>
          withSnapshotUpdate(state, { unreadOnly: !state.unreadOnly }),
        ),

      clearAllFilters: () =>
        set((state) =>
          withSnapshotUpdate(state, {
            selectedFilterLabelIds:
              INITIAL_FILTER_SORT_STATE.selectedFilterLabelIds,
            showNoLabelsFilter: INITIAL_FILTER_SORT_STATE.showNoLabelsFilter,
            selectedDueDateFilters:
              INITIAL_FILTER_SORT_STATE.selectedDueDateFilters,
            selectedPriorityFilters:
              INITIAL_FILTER_SORT_STATE.selectedPriorityFilters,
            selectedCardStatusFilter:
              INITIAL_FILTER_SORT_STATE.selectedCardStatusFilter,
            showNoMembersFilter: INITIAL_FILTER_SORT_STATE.showNoMembersFilter,
            selectedFilterMemberIds:
              INITIAL_FILTER_SORT_STATE.selectedFilterMemberIds,
            unreadOnly: INITIAL_FILTER_SORT_STATE.unreadOnly,
          }),
        ),

      clearMemberFilters: () =>
        set((state) =>
          withSnapshotUpdate(state, {
            showNoMembersFilter: INITIAL_FILTER_SORT_STATE.showNoMembersFilter,
            selectedFilterMemberIds:
              INITIAL_FILTER_SORT_STATE.selectedFilterMemberIds,
          }),
        ),

      retainMemberFilters: (allowedIds) => {
        const { selectedFilterMemberIds } = get();
        const retained = selectedFilterMemberIds.filter((id) =>
          allowedIds.includes(id),
        );
        if (retained.length === selectedFilterMemberIds.length) {
          return;
        }
        set((state) =>
          withSnapshotUpdate(state, { selectedFilterMemberIds: retained }),
        );
      },

      setSortField: (field) =>
        set((state) => withSnapshotUpdate(state, { sortField: field })),

      setSortOrder: (order) =>
        set((state) => withSnapshotUpdate(state, { sortOrder: order })),

      initSort: (config) =>
        set((state) =>
          withSnapshotUpdate(state, {
            sortField: config.sortField,
            sortOrder: config.sortOrder,
          }),
        ),
    }),
    {
      name: 'dobox-filter-sort',
      storage: createJSONStorage(createIndexedDBStorage),
      partialize: (state) => ({
        taskGroupFilterSortMap: state.taskGroupFilterSortMap,
        lastActiveTaskGroupId: state.lastActiveTaskGroupId,
        lastActiveTeamId: state.lastActiveTeamId,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
